import { useEffect, useLayoutEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { MotionLayer } from "./MotionLayer";

const navItems = [
  ["/about", "About"],
  ["/work", "Work"],
  ["/services", "Services"],
  ["/hello-marky-chat", "Hello Marky Chat"],
] as const;

const pageTitles: Record<string, string> = {
  "/": "Hello Marky | Custom Systems and AI Automation",
  "/work": "Work | Hello Marky",
  "/services": "Services | Hello Marky",
  "/hello-marky-chat": "Hello Marky Chat | A Connected AI Conversation System",
  "/about": "About | Hello Marky",
  "/contact": "Start a Conversation | Hello Marky",
};

function Brand() {
  return (
    <Link className="brand" to="/" aria-label="Hello Marky home">
      <img src="/brand/marky-dark.svg" alt="" aria-hidden="true" />
      <span>HELLO MARKY</span>
    </Link>
  );
}

export function SiteLayout() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
    document.title = pageTitles[location.pathname]
      ?? (location.pathname.startsWith("/work/") ? "Case Study | Hello Marky" : "Hello Marky");
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (!menuOpen) return;

    const desktop = window.matchMedia("(min-width: 901px)");
    if (desktop.matches) {
      setMenuOpen(false);
      return;
    }

    // Lock the viewport, not body: body overflow creates a new scroll
    // container and makes the sticky header disappear on scrolled pages.
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    const closeOnDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    desktop.addEventListener("change", closeOnDesktop);

    return () => {
      root.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
      desktop.removeEventListener("change", closeOnDesktop);
    };
  }, [menuOpen]);

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <header className="site-header site-header--world">
        <Brand />
        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map(([href, label]) => (
            <NavLink key={href} to={href}>{label}</NavLink>
          ))}
        </nav>
        <Link className="header-contact" to="/contact">Tell us what feels heavy <span aria-hidden="true">↗</span></Link>
        <button
          type="button"
          className="menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="mobile-navigation"
          onClick={() => setMenuOpen((value) => !value)}
        >
          <span>{menuOpen ? "Close" : "Menu"}</span>
          <i aria-hidden="true" />
        </button>
      </header>

      <div id="mobile-navigation" className={`mobile-menu ${menuOpen ? "mobile-menu--open" : ""}`} aria-hidden={!menuOpen}>
        <nav aria-label="Mobile navigation">
          {navItems.map(([href, label], index) => (
            <NavLink key={href} to={href} tabIndex={menuOpen ? 0 : -1} onClick={() => setMenuOpen(false)}>
              <span>0{index + 1}</span>{label}
            </NavLink>
          ))}
          <Link className="mobile-contact" to="/contact" tabIndex={menuOpen ? 0 : -1} onClick={() => setMenuOpen(false)}>Tell us what feels heavy <span aria-hidden="true">↗</span></Link>
        </nav>
        <div className="mobile-menu-marky" aria-hidden="true"><img src="/brand/marky-dark.svg" alt="" /></div>
      </div>

      <MotionLayer routeKey={location.pathname}>
        <Outlet />
      </MotionLayer>

      <footer className="site-footer">
        <div className="footer-statement">
          <Brand />
          <p>Custom systems and AI automation for a lighter way to run the business.</p>
        </div>
        <nav aria-label="Footer navigation">
          <span>Explore</span>
          {navItems.map(([href, label]) => <Link key={href} to={href}>{label}</Link>)}
        </nav>
        <div className="footer-contact">
          <span>Start somewhere useful</span>
          <Link to="/contact">Tell us what feels heavy</Link>
          <a href="mailto:hi@hellomarky.com">hi@hellomarky.com</a>
        </div>
        <div className="footer-bottom">
          <span>© 2026 Hello Marky</span>
          <span>Built for calmer business days.</span>
        </div>
      </footer>
    </div>
  );
}
